import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import {
  COLOR_CHART_DEMAND_FIGURE_ELEVATED,
  COLOR_CHART_DEMAND_FIGURE_LOW,
  COLOR_CHART_DEMAND_FIGURE_NORMAL,
  getCssVariableString,
} from 'frontend-utils/utils/css-variable';
import type { Day } from 'lumen/models';
import {
  MI_SEARCHES_TYPE_FLIGHTS_BUSINESS,
  MI_SEARCHES_TYPE_FLIGHTS_LEISURE,
  MI_SEARCHES_TYPE_HOTELS_BUSINESS,
  MI_SEARCHES_TYPE_HOTELS_LEISURE,
} from 'lumen/utils/constants/general-constants';
import {
  SEARCHSUMMARIES_SEARCHES_LEVEL_HIGHER,
  SEARCHSUMMARIES_SEARCHES_LEVEL_LOWER,
  SEARCHSUMMARIES_SEARCHES_LEVEL_NORMAL,
} from 'lumen/utils/constants/market-insight-constants';

// Threshold depends which "smaller" changes we can mark as normal
// 0.05 is a factor decided by Data Science team
const DEMAND_THRESHOLD = 0.05;

// Note: Searchsummary id examples:
// 0,2020-05-11,latest
// 0,2020-05-19,2020-05-04
// <type>,<day>,<extract date>
export default class Searchsummary extends Model {
  [Type] = 'searchsummary' as const;

  @belongsTo('day', { async: false, inverse: 'searchsummaries' })
  declare day: Day;

  @attr('number') declare benchmark: number;
  @attr('string') declare benchmarkMessage: string;
  @attr('string') declare dateAggregation: string;
  @attr('dayjs') declare extractDate: NonNullable<DayjsTransformReturn>;
  @attr('boolean') declare isLatest: boolean;
  @attr('string') declare message: string;
  @attr('number') declare mostSearchedLos: number;
  // Number of searches for the current day (aggregated figure of the last 7 days worth of data received for that staydate) (also includes staythroughs)
  @attr('number') declare numSearches: number;
  // Value that represents the "max value" for the bar indicator of Overall searches (precalculated in backend)
  @attr('number') declare numSearchesUpperBound: number;
  @attr('number') declare searchLevel: number;
  @attr('number') declare searchVolume: number;
  @attr() declare topCountries: unknown;
  // Flights / hotels / ...?
  @attr('number') declare type: number;

  @service declare intl: Services['intl'];
  @service declare miSearches: Services['mi-searches'];

  // used in the formatting for the benchmark bar values
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get benchmarkCorrected() {
    if (
      !this.isBenchmarkMissing &&
      Number.isFinite(this.numSearchesUpperBound)
    ) {
      return Math.min(this.benchmark, this.numSearchesUpperBound);
    }

    return null;
  }

  // We need to do some extra transforming for the benchmark -> the component expects percentages, but we get # of searches from the API
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get benchmarkValues() {
    const result: { benchmark: number | null; searches: number } = {
      searches: 0,
      benchmark: null,
    };

    if (this.numSearchesCorrected) {
      const numSearchesToUse = this.numSearchesCorrected;

      result.searches = (numSearchesToUse / this.numSearchesUpperBound) * 100;
    }

    if (this.benchmarkCorrected) {
      result.benchmark =
        (this.benchmarkCorrected / this.numSearchesUpperBound) * 100;
    }

    return result;
  }

  // https://otainsight.slack.com/archives/GGLM7LJ4W/p1602581917008900
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandLevel() {
    // Calculation determined by Data Science
    return (this.numSearches - this.benchmark) / this.numSearchesUpperBound;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandLevelColor() {
    if (this.numSearchesAsLevel === SEARCHSUMMARIES_SEARCHES_LEVEL_LOWER) {
      return getCssVariableString(COLOR_CHART_DEMAND_FIGURE_LOW);
    }
    if (this.numSearchesAsLevel === SEARCHSUMMARIES_SEARCHES_LEVEL_HIGHER) {
      return getCssVariableString(COLOR_CHART_DEMAND_FIGURE_ELEVATED);
    }
    return getCssVariableString(COLOR_CHART_DEMAND_FIGURE_NORMAL);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedMostSearchedLos() {
    if (this.isSummaryMissing || this.numSearches === 0) {
      return this.intl.t('general.missing');
    }

    return this.mostSearchedLos;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedSearchVolume() {
    if (this.isSummaryMissing) {
      return this.intl.t('general.missing');
    }

    return this.intl.formatNumber(this.searchVolume, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBenchmarkMissing() {
    return this.benchmarkMessage === 'general.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFlightBusinessSearchSummary() {
    return this.type === MI_SEARCHES_TYPE_FLIGHTS_BUSINESS;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFlightLeisureSearchSummary() {
    return this.type === MI_SEARCHES_TYPE_FLIGHTS_LEISURE;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isHotelBusinessSearchSummary() {
    return this.type === MI_SEARCHES_TYPE_HOTELS_BUSINESS;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isHotelLeisureSearchSummary() {
    return this.type === MI_SEARCHES_TYPE_HOTELS_LEISURE;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSummaryIncomplete() {
    return this.message === 'search_summary.incomplete_week';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSummaryMissing() {
    return this.message === 'general.missing';
  }

  // Note: this logic is repeated in the reports framework as well for the MI reports
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numSearchesAsBadgeClass() {
    if (Number.isFinite(this.numSearchesAsLevel)) {
      if (this.numSearchesAsLevel === SEARCHSUMMARIES_SEARCHES_LEVEL_NORMAL) {
        return 'neutral';
      }
      if (this.numSearchesAsLevel === SEARCHSUMMARIES_SEARCHES_LEVEL_HIGHER) {
        return 'danger';
      }
      if (this.numSearchesAsLevel === SEARCHSUMMARIES_SEARCHES_LEVEL_LOWER) {
        return 'info';
      }
    }

    return 'neutral';
  }

  // Possible values: Higher, Lower, Normal
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numSearchesAsLabel() {
    if (Number.isFinite(this.numSearchesAsLevel)) {
      return this.intl.t(
        `market_insight.searchsummaries_label.level${this.numSearchesAsLevel}`,
      );
    }

    return this.intl.t('general.missing');
  }

  // If we do changes here, always check if it's in sync with the reports!
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numSearchesAsLevel() {
    if (!this.isSummaryMissing) {
      let level = SEARCHSUMMARIES_SEARCHES_LEVEL_NORMAL;
      if (Math.abs(this.demandLevel) < DEMAND_THRESHOLD) {
        level = SEARCHSUMMARIES_SEARCHES_LEVEL_NORMAL;
      } else if (this.demandLevel > 0) {
        level = SEARCHSUMMARIES_SEARCHES_LEVEL_HIGHER;
      } else if (this.demandLevel < 0) {
        level = SEARCHSUMMARIES_SEARCHES_LEVEL_LOWER;
      }

      return level;
    }

    return null;
  }

  // Possible values: high, low, normal
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numSearchesAsTooltipLabel() {
    if (Number.isFinite(this.numSearchesAsLevel)) {
      if (this.numSearchesAsLevel === SEARCHSUMMARIES_SEARCHES_LEVEL_NORMAL) {
        return this.intl
          .t('market_insight.rate_level.level3')
          .toString()
          .toLowerCase();
      }
      if (this.numSearchesAsLevel === SEARCHSUMMARIES_SEARCHES_LEVEL_HIGHER) {
        return this.intl
          .t('market_insight.rate_level.level5')
          .toString()
          .toLowerCase();
      }
      if (this.numSearchesAsLevel === SEARCHSUMMARIES_SEARCHES_LEVEL_LOWER) {
        return this.intl
          .t('market_insight.rate_level.level2')
          .toString()
          .toLowerCase();
      }
    }

    return this.intl.t('general.missing').toString();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numSearchesCorrected() {
    if (
      Number.isFinite(this.numSearches) &&
      Number.isFinite(this.numSearchesUpperBound)
    ) {
      return Math.min(this.numSearches, this.numSearchesUpperBound);
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get searchSummaryTitle() {
    const areHotelBusinessSearchesSupported =
      this.miSearches.areHotelBusinessSearchesSupportedForTheCurrentDestination;
    const areFlightBusinessSearchesSupported =
      this.miSearches
        .areFlightBusinessSearchesSupportedForTheCurrentDestination;

    const metaOTA = `${this.intl.t('general.meta')}/${this.intl.t('general.ota')}`;
    const metaOTAGDS = `${metaOTA}/${this.intl.t('general.GDS')}`;

    const hotelSearchPatterns = this.intl.t(
      'market_insight.detail_pane.section_title.hotel_search_patterns',
    );
    const flightSearchPatterns = this.intl.t(
      'market_insight.detail_pane.section_title.flight_search_patterns',
    );

    if (this.isHotelLeisureSearchSummary) {
      if (
        areHotelBusinessSearchesSupported ||
        areFlightBusinessSearchesSupported
      ) {
        if (!areHotelBusinessSearchesSupported) {
          return `${hotelSearchPatterns} (${metaOTAGDS})`;
        }
        return `${hotelSearchPatterns} (${metaOTA})`;
      }
      return hotelSearchPatterns;
    }
    if (this.isHotelBusinessSearchSummary) {
      return `${hotelSearchPatterns} (${this.intl.t('general.GDS')})`;
    }
    if (this.isFlightLeisureSearchSummary) {
      if (
        areHotelBusinessSearchesSupported ||
        areFlightBusinessSearchesSupported
      ) {
        if (!areFlightBusinessSearchesSupported) {
          return `${flightSearchPatterns} (${metaOTAGDS})`;
        }
        return `${flightSearchPatterns} (${metaOTA})`;
      }
      return flightSearchPatterns;
    }
    return `${flightSearchPatterns} (${this.intl.t('general.GDS')})`;
  }
}
